import * as React from 'react';
import {noop} from 'lodash';
import {onButtonConfirm} from '@packages/utilities/compose-events';
import {useSize} from '../../themes';
import {TabNewIcon} from '../../icons';
import {LinkContent} from './LinkContent';
import {LinkView, ButtonMasqueradingAsLinkView} from './Link.styles';
import type {LinkProps} from './_types';

export const LinkBase = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      children,
      disabled,
      href,
      isDisabled,
      isExternal,
      onClick = noop,
      onKeyDown: onKeyDownProp,
      onKeyUp: onKeyUpProp,
      prefix,
      role: roleProp,
      shouldInheritFont,
      shouldShowUnderline,
      shouldShowUnderlineOnHover,
      size: sizeProp,
      suffix,
      tabIndex: tabIndexProp,
      target: targetProp,
      truncation,
      ...props
    }: LinkProps & {
      truncation?: React.ComponentProps<typeof LinkView>['truncation'];
    },
    ref
  ) => {
    const size = useSize(sizeProp);

    const isLink = Boolean(href);
    const isDisabledButton = !isLink && (disabled || isDisabled);

    const Component = isLink ? LinkView : ButtonMasqueradingAsLinkView;
    const role = isLink ? undefined : 'button';
    const tabIndex = isLink ? undefined : 0;
    const {
      onKeyDown: onKeyDownButton,
      onKeyUp: onKeyUpButton,
      // @ts-expect-error - onButtonConfirm() works on SyntheticEvent as well as Event
    } = onButtonConfirm(onClick);

    const handleOnKeyDown = (event: any) => {
      onKeyDownProp && onKeyDownProp(event);

      if (isLink || isDisabledButton) return;

      if (!event.defaultPrevented) {
        onKeyDownButton(event);
      }
    };
    const handleOnKeyUp = (event: any) => {
      onKeyUpProp && onKeyUpProp(event);

      if (isLink || isDisabledButton) return;

      if (!event.defaultPrevented) {
        onKeyUpButton(event);
      }
    };
    const handleOnClick = (event: any) => {
      if (isDisabledButton) return;

      if (!event.defaultPrevented) {
        onClick(event);
      }
    };

    const shouldShowUnderlineValue = shouldShowUnderline ? 'show' : 'hide';
    const shouldDecorate =
      shouldShowUnderline !== undefined ? shouldShowUnderlineValue : undefined;
    const shouldShowUnderlineOnHoverValue = shouldShowUnderlineOnHover
      ? 'show'
      : 'hide';
    const shouldDecorateOnHover =
      shouldShowUnderlineOnHover !== undefined
        ? shouldShowUnderlineOnHoverValue
        : undefined;

    const externalSuffix = isExternal ? TabNewIcon : null;
    const suffixElement = suffix || externalSuffix;

    const target = isExternal ? '_blank' : targetProp;

    return (
      <Component
        aria-disabled={isDisabledButton}
        data-sc="link"
        // @ts-expect-error - @SPRING_CLEANING: Link uses a styleAtom that has the decoration and hoverDecoration variants, but TS is complaining here that they don't exist
        decoration={shouldDecorate}
        hoverDecoration={shouldDecorateOnHover}
        href={isLink ? href : undefined}
        isDisabled={isDisabled || disabled}
        onClick={handleOnClick}
        onKeyDown={handleOnKeyDown}
        onKeyUp={handleOnKeyUp}
        ref={ref}
        role={roleProp ?? role}
        shouldInheritFont={shouldInheritFont}
        size={size}
        tabIndex={tabIndexProp ?? tabIndex}
        target={target && isLink ? target : undefined}
        truncation={isLink ? truncation : undefined}
        {...props}
      >
        <LinkContent prefix={prefix} suffix={suffixElement}>
          {children}
        </LinkContent>
      </Component>
    );
  }
);
